import { Checkbox } from "@/components/ui/checkbox";
import { useSetMessageTask } from "@/features/messages/hooks/use-messages";
import type { ClientMessage } from "@/features/messages/types";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * The follow-up steps the AI proposed for a request, as a checklist the team
 * works through. The AI only ever adds to it (a later call about the same matter
 * proposes what is new); ticking is the team's, and nothing else unticks a step.
 */
export function MessageTasks({
  message,
  canManage,
}: {
  message: ClientMessage;
  canManage: boolean;
}) {
  const { t } = useTranslation("messages");
  const setTask = useSetMessageTask();
  const tasks = message.tasks || [];
  if (!tasks.length) return null;

  const done = tasks.filter((task) => task.done).length;

  return (
    <section className="space-y-2">
      <h3 className="flex items-baseline justify-between text-sm font-medium text-muted-foreground">
        {t("view.tasks")}
        <span className="text-xs font-normal tabular-nums">
          {t("view.tasksDone", { done, total: tasks.length })}
        </span>
      </h3>
      <ul className="space-y-1.5">
        {tasks.map((task) => {
          const id = `task-${message.id}-${task.id}`;
          return (
            <li key={task.id} className="flex items-start gap-2.5">
              <Checkbox
                id={id}
                className="mt-0.5"
                checked={task.done}
                disabled={!canManage}
                onCheckedChange={(checked) =>
                  setTask.mutate(
                    { id: message.id, taskId: task.id, done: checked === true },
                    {
                      onError: (error) =>
                        toast.error(
                          error instanceof Error
                            ? error.message
                            : t("view.taskFailed"),
                        ),
                    },
                  )
                }
              />
              <label
                htmlFor={id}
                className={cn(
                  "text-sm leading-snug",
                  canManage && "cursor-pointer",
                  task.done && "text-muted-foreground line-through",
                )}
              >
                {task.label}
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
